import React from "react";
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import WebURL from "@/urls";
import { EmployeeOvertimeItem, EmployeeOvertimeListData } from "@/services/att/services/AnalyticsService";

interface Props {
  data: EmployeeOvertimeListData | null;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

/**
 * Safely format OT hours as a fixed-decimal string.
 * The backend may return hours as a number, a string (e.g. "8.5"), or omit them
 * entirely (null / undefined), so coerce with Number() and fall back to "0.0"
 * when the value is not a valid number.
 */
const formatOTHours = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "0.0";
  const num = typeof value === "number" ? value : Number(value);
  return Number.isNaN(num) ? "0.0" : num.toFixed(1);
};

const OvertimeEmployeeList: React.FC<Props> = ({ data, loading, page, pageSize, onPageChange }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const totalPages = data ? Math.ceil(data.total / pageSize) : 1;

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #EBEEF2",
        p: 1.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 360,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#333333" }}>
          {t("Dashboard.Overtime Employees (This Week)")}
        </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", color: "#555555", "&:hover": { color: "#3498DB" } }}
              onClick={() => navigate(WebURL.ATT_REPORT_OVERTIME_ANALYSIS)}
            >
              <RemoveRedEyeIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 400 }}>{t("Dashboard.View all")}</Typography>
            </Box>
          </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table size="small" sx={{ minWidth: 280 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#888", p: "10px 14px", borderBottom: "1px solid #EBEEF2", backgroundColor: "#FAFAFA" }}>{t("Dashboard.Person Id")}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#888", p: "10px 14px", borderBottom: "1px solid #EBEEF2", backgroundColor: "#FAFAFA" }}>{t("Dashboard.Person Name")}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: "#888", p: "10px 14px", borderBottom: "1px solid #EBEEF2", backgroundColor: "#FAFAFA" }}>{t("Dashboard.T1 OT Hours")}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12, color: "#888", p: "10px 14px", borderBottom: "1px solid #EBEEF2", backgroundColor: "#FAFAFA" }}>{t("Dashboard.T2 OT Hours")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.list ?? []).map((row: EmployeeOvertimeItem) => (
                <TableRow key={row.personId} sx={{ "&:hover": { backgroundColor: "#F5F5F5" } }}>
                  <TableCell sx={{ fontSize: 13, color: "#333", p: "10px 14px", borderBottom: "1px solid #F0F0F0" }}>{row.personId}</TableCell>
                  <TableCell sx={{ fontSize: 13, color: "#333", p: "10px 14px", borderBottom: "1px solid #F0F0F0" }}>{row.personName}</TableCell>
                  <TableCell align="center" sx={{ fontSize: 13, fontWeight: 600, color: "#00A8E8", p: "10px 14px", borderBottom: "1px solid #F0F0F0" }}>{formatOTHours(row.t1OTHours)}</TableCell>
                  <TableCell align="center" sx={{ fontSize: 13, fontWeight: 600, color: "#FF6B6B", p: "10px 14px", borderBottom: "1px solid #F0F0F0" }}>{formatOTHours(row.t2OTHours)}</TableCell>
                </TableRow>
              ))}
              {(!data?.list || data.list.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3, color: "#999" }}>
                    {t("Dashboard.No data available")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {data && data.total > pageSize && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, pt: 1.5 }}>
          <Button
            size="small"
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            sx={{ fontSize: 12, textTransform: "none", minWidth: 60 }}
          >
            {t("Dashboard.Previous")}
          </Button>
          <Typography sx={{ fontSize: 12, color: "#888", display: "flex", alignItems: "center" }}>
            {page} / {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            sx={{ fontSize: 12, textTransform: "none", minWidth: 60 }}
          >
            {t("Dashboard.Next")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OvertimeEmployeeList;